/**
 * Ocean material — binds the shared Gerstner shader to the wave field the simulation is
 * using, plus the environment palette. Rebuilding the wave uniforms every frame is cheap
 * (16 vec4s) and means weather transitions need no shader recompilation.
 */

import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { ShaderStore } from '@babylonjs/core/Engines/shaderStore';
import { Vector2, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';

import oceanVert from '../../shaders/ocean.vert';
import oceanFrag from '../../shaders/ocean.frag';

import { packWavesForShader, WAVE_COUNT, type WaveField } from '../../sim/waves';
import type { WeatherState } from '../../sim/weather';
import type { Environment } from '../environment';
import type { RigOceanFlood } from '../rig/rigBuilder';
import { WIDE_FOV_DEG } from '../constants';

let registered = false;
function registerShaders(): void {
  if (registered) return;
  ShaderStore.ShadersStore['deepwaterOceanVertexShader'] = oceanVert;
  ShaderStore.ShadersStore['deepwaterOceanFragmentShader'] = oceanFrag;
  registered = true;
}

export class OceanMaterial {
  readonly material: ShaderMaterial;
  private readonly waveBufferA = new Float32Array(WAVE_COUNT * 4);
  private readonly waveBufferB = new Float32Array(WAVE_COUNT * 4);
  private rigFloodBaseStrength = 0;
  private rigFloodPowerScale = 1;

  constructor(scene: Scene, detailNormal: Texture, foamTex: Texture) {
    registerShaders();

    this.material = new ShaderMaterial(
      'oceanMat',
      scene,
      { vertex: 'deepwaterOcean', fragment: 'deepwaterOcean' },
      {
        attributes: ['position', 'uv'],
        uniforms: [
          'viewProjection',
          'uTime',
          'uCameraPos',
          'uCameraFov',
          'uMagnification',
          'uViewportHeight',
          'waveA',
          'waveB',
          'uDeepColor',
          'uShallowColor',
          'uScatterColor',
          'uScatterStrength',
          'uWindDir',
          'uWindSpeed',
          'uDetailStrength',
          'uSeaState',
          'uFoamAmount',
          'uFoamColor',
          'uFogColor',
          'uFogDensity',
          'uHorizonHaze',
          'uRain',
          'uFogAmount',
          'uZenithColor',
          'uHorizonColor',
          'uSunColor',
          'uSunDir',
          'uSunGlowPower',
          'uSunGlowIntensity',
          'uHorizonFalloff',
          'uRigFloodPos0',
          'uRigFloodDir0',
          'uRigFloodPos1',
          'uRigFloodDir1',
          'uRigFloodColor',
          'uRigFloodStrength',
          'uNearWaterTint',
          'uNearWaterRange',
          'uSearchPos',
          'uSearchDir',
          'uSearchStrength',
          'uExposure',
          'uSwellHs',
          'uMistAir',
          'uMistNear',
          'uMistNearRange',
          'uMistSea',
          'uMistHeight',
        ],
        samplers: ['uDetailNormal', 'uFoamTex'],
      },
    );

    this.material.setTexture('uDetailNormal', detailNormal);
    this.material.setTexture('uFoamTex', foamTex);
    this.material.backFaceCulling = false;
    // The searchlight is off until something aims it. An unset vec3 uniform would
    // otherwise leave a beam pinned at the world origin on the first frame.
    this.setSearchlight(null, Vector3.Zero(), Vector3.Forward());
  }

  /**
   * Aim the trainable searchlight, or pass a null strength to extinguish it.
   *
   * Kept out of the scene's light list on purpose: this is an analytic term in the ocean
   * shader, so it costs no draw calls and does not compete for the six-light material
   * budget that the rig's floods already fill.
   */
  setSearchlight(strength: number | null, position: Vector3, direction: Vector3): void {
    this.material.setFloat('uSearchStrength', strength ?? 0);
    this.material.setVector3('uSearchPos', position);
    this.material.setVector3('uSearchDir', direction);
  }

  /** Bind the two sea-facing rig floods without adding scene-light draw cost. */
  setRigFloods(floods: readonly RigOceanFlood[]): void {
    const first = floods[0];
    const second = floods[1];
    if (!first || !second) {
      throw new Error('ocean shading requires two rig floodlight definitions');
    }
    this.material.setVector3('uRigFloodPos0', first.position);
    this.material.setVector3('uRigFloodDir0', first.direction);
    this.material.setVector3('uRigFloodPos1', second.position);
    this.material.setVector3('uRigFloodDir1', second.direction);
  }

  /** Dim the analytic sea pools with the physical fixtures during a rig brownout. */
  setRigFloodPower(scale: number): void {
    this.rigFloodPowerScale = Math.max(0, Math.min(1, scale));
    this.material.setFloat(
      'uRigFloodStrength',
      this.rigFloodBaseStrength * this.rigFloodPowerScale,
    );
  }

  /** Upload the wave field. Call whenever the field is rebuilt (sea state changes). */
  setWaveField(field: WaveField): void {
    const packed = packWavesForShader(field);
    for (let i = 0; i < WAVE_COUNT; i++) {
      this.waveBufferA[i * 4 + 0] = packed[i * 8 + 0] as number;
      this.waveBufferA[i * 4 + 1] = packed[i * 8 + 1] as number;
      this.waveBufferA[i * 4 + 2] = packed[i * 8 + 2] as number;
      this.waveBufferA[i * 4 + 3] = packed[i * 8 + 3] as number;
      this.waveBufferB[i * 4 + 0] = packed[i * 8 + 4] as number;
      this.waveBufferB[i * 4 + 1] = packed[i * 8 + 5] as number;
      this.waveBufferB[i * 4 + 2] = packed[i * 8 + 6] as number;
      this.waveBufferB[i * 4 + 3] = 0;
    }
    this.material.setFloat('uSwellHs', Math.max(field.significantHeight, 0.5));
    this.material.setArray4('waveA', this.waveBufferA as unknown as number[]);
    this.material.setArray4('waveB', this.waveBufferB as unknown as number[]);
  }

  setEnvironment(env: Environment, weather: WeatherState): void {
    const m = this.material;
    m.setColor3('uDeepColor', env.deepColor);
    m.setColor3('uShallowColor', env.shallowColor);
    m.setColor3('uScatterColor', env.scatterColor);
    m.setFloat('uScatterStrength', env.scatterStrength);
    m.setColor3('uFoamColor', env.foamColor);
    m.setFloat('uFoamAmount', env.foamAmount);
    m.setColor3('uFogColor', env.fogColor);
    m.setFloat('uFogDensity', env.fogDensity);
    m.setFloat('uHorizonHaze', env.horizonHaze);
    m.setFloat('uRain', weather.rain);
    m.setFloat('uFogAmount', weather.fogDensity);
    m.setFloat('uDetailStrength', env.detailStrength);
    m.setFloat('uSeaState', weather.seaState);
    m.setVector2('uWindDir', new Vector2(Math.sin(weather.windDir), Math.cos(weather.windDir)));
    m.setFloat('uWindSpeed', weather.windSpeed);

    m.setColor3('uZenithColor', env.zenithColor);
    m.setColor3('uHorizonColor', env.horizonColor);
    m.setColor3('uSunColor', env.sunColor);
    m.setVector3('uSunDir', env.sunDir);
    m.setFloat('uSunGlowPower', env.sunGlowPower);
    m.setFloat('uSunGlowIntensity', env.sunGlowIntensity);
    m.setFloat('uHorizonFalloff', env.horizonFalloff);
    m.setFloat('uMistAir', env.mistAirDensity);
    m.setFloat('uMistNear', env.mistNearDensity);
    m.setFloat('uMistNearRange', env.mistNearRange);
    m.setFloat('uMistSea', env.mistSeaDensity);
    m.setFloat('uMistHeight', env.mistHeightScale);
    m.setColor3('uRigFloodColor', env.rigLightColor);
    this.rigFloodBaseStrength = env.rigLightStrength;
    this.setRigFloodPower(this.rigFloodPowerScale);
    m.setColor3('uNearWaterTint', env.nearWaterTint);
    m.setFloat('uNearWaterRange', env.nearWaterRange);
    m.setFloat('uExposure', env.exposure);
  }

  setFrame(time: number, cameraPos: Vector3, cameraFov: number, viewportHeight: number): void {
    this.material.setFloat('uTime', time);
    this.material.setVector3('uCameraPos', cameraPos);
    this.material.setFloat('uCameraFov', cameraFov);
    const wideHalfAngle = (WIDE_FOV_DEG * Math.PI) / 360;
    const magnification = Math.tan(wideHalfAngle) / Math.tan(cameraFov * 0.5);
    this.material.setFloat('uMagnification', Math.max(1, magnification));
    this.material.setFloat('uViewportHeight', viewportHeight);
  }

  dispose(): void {
    this.material.dispose();
  }
}
